import { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import apiClient from '../api/apiClient';
import quranApi from '../api/quranApi';
import { useTheme } from '../context/ThemeContext';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const StatsPage = () => {
    const { theme } = useTheme();
    const [taskStats, setTaskStats] = useState(null);
    const [dailyStats, setDailyStats] = useState(null);
    const [quranStats, setQuranStats] = useState(null);
    const [quranSummary, setQuranSummary] = useState({ totalAyahs: 0, completedSurahs: 0 });
    const [streak, setStreak] = useState(0);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('week'); // 'week' or 'month'
    const [quranTimeRange, setQuranTimeRange] = useState('month'); // 'month' or 'year'
    const [quranViewType, setQuranViewType] = useState('ayahs'); // 'ayahs' or 'surahs'

    const [fullHistory, setFullHistory] = useState([]); // Store raw data
    const [fullQuranStats, setFullQuranStats] = useState(null); // Store raw quran data
    const [surahList, setSurahList] = useState([]); // Store list of 114 surahs

    const actionLabels = {
        prayer: 'Prière',
        tasbih: 'Tasbih',
        recitation: 'Coran',
        douaa: 'Douaa',
        sadaqa: 'Sadaqa',
    };

    // Define colors based on theme
    const getThemeColors = (activeTheme) => {
        if (activeTheme === 'girly') {
            return {
                primary: '#E84393',
                secondary: '#FAB1A0',
                accent: '#6C5CE7',
                chart: {
                    completed: '#E84393',
                    pending: '#FAB1A0',
                    ayahs: '#E84393',
                    ayahsBg: 'rgba(232, 67, 147, 0.2)',
                    surahs: '#FAB1A0',
                    surahsBg: 'rgba(250, 177, 160, 0.2)'
                }
            };
        } else if (activeTheme === 'neutral') {
            return {
                primary: '#2D3436',
                secondary: '#636E72',
                accent: '#B2BEC3',
                chart: {
                    completed: '#2D3436',
                    pending: '#B2BEC3',
                    ayahs: '#2D3436',
                    ayahsBg: 'rgba(45, 52, 54, 0.2)',
                    surahs: '#636E72',
                    surahsBg: 'rgba(99, 110, 114, 0.2)'
                }
            };
        }
        // Default
        return {
            primary: '#1B7A6E',
            secondary: '#D4AF37',
            accent: '#E8B4B8',
            chart: {
                completed: '#1B7A6E',
                pending: '#D4AF37',
                ayahs: '#1B7A6E',
                ayahsBg: 'rgba(27, 122, 110, 0.2)',
                surahs: '#D4AF37',
                surahsBg: 'rgba(212, 175, 55, 0.2)'
            }
        };
    };

    const themeColors = getThemeColors(theme);

    const actionColors = {
        prayer: 'rgba(255, 99, 132, 0.7)',
        tasbih: 'rgba(54, 162, 235, 0.7)',
        recitation: 'rgba(255, 206, 86, 0.7)',
        douaa: 'rgba(75, 192, 192, 0.7)',
        sadaqa: 'rgba(153, 102, 255, 0.7)',
    };

    const actionBorders = {
        prayer: 'rgba(255, 99, 132, 1)',
        tasbih: 'rgba(54, 162, 235, 1)',
        recitation: 'rgba(255, 206, 86, 1)',
        douaa: 'rgba(75, 192, 192, 1)',
        sadaqa: 'rgba(153, 102, 255, 1)',
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [tasksRes, dailyRes, quranRes] = await Promise.all([
                    apiClient.get('/tasks'),
                    apiClient.get('/daily-actions/history?days=30'),
                    apiClient.get('/quran/stats'),
                ]);

                processTaskStats(Array.isArray(tasksRes.data) ? tasksRes.data : []);
                setFullHistory(Array.isArray(dailyRes.data) ? dailyRes.data : []); // Store full 30 days
                processDailyStats(dailyRes.data, timeRange); // Process with current range

                setFullQuranStats(quranRes.data);
                processQuranStats(quranRes.data, quranTimeRange);

                setLoading(false);
            } catch (error) {
                console.error('Error fetching stats', error);
                setLoading(false);
            }
        };

        const fetchSurahs = async () => {
            try {
                const list = await quranApi.getAllSurahs();
                setSurahList(list);
            } catch (error) {
                console.error('Error fetching surahs', error);
            }
        };

        fetchData();
        fetchSurahs();
    }, []);

    // Re-process stats when theme changes to update colors
    useEffect(() => {
        if (taskStats) {
            // Need to update colors for existing data
            const total = taskStats.datasets[0].data[0] + taskStats.datasets[0].data[1];
            const completed = taskStats.datasets[0].data[0];
            const pending = taskStats.datasets[0].data[1];
            setTaskStats({
                labels: ['Terminées', 'En attente'],
                datasets: [
                    {
                        data: [completed, pending],
                        backgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                        hoverBackgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                    },
                ],
            });
        }
    }, [theme]);

    // Re-process daily stats when timeRange changes
    useEffect(() => {
        if (fullHistory.length > 0) {
            processDailyStats(fullHistory, timeRange);
        }
    }, [timeRange, fullHistory]);

    // Re-process quran stats when quranTimeRange or quranViewType or theme changes
    useEffect(() => {
        if (fullQuranStats && surahList.length > 0) {
            processQuranStats(fullQuranStats, quranTimeRange);
        }
    }, [quranTimeRange, quranViewType, fullQuranStats, surahList, theme]);

    const processTaskStats = (tasks) => {
        const total = tasks.length;
        const completed = tasks.filter((t) => t.completed).length;
        const pending = total - completed;

        setTaskStats({
            labels: ['Terminées', 'En attente'],
            datasets: [
                {
                    data: [completed, pending],
                    backgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                    hoverBackgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                },
            ],
        });
    };

    const processDailyStats = (history, range) => {
        const daysToProcess = range === 'week' ? 7 : 30;
        const selectedHistory = history.slice(-daysToProcess);

        const labels = selectedHistory.map(d => new Date(d.date).toLocaleDateString('fr-FR', {
            weekday: 'short',
            day: range === 'month' ? 'numeric' : undefined
        }));

        // Identify all unique action types present in data or defaults
        const actionTypes = Object.keys(actionLabels);

        const datasets = actionTypes.map(type => {
            return {
                label: actionLabels[type],
                data: selectedHistory.map(day => {
                    const action = day.actions.find(a => a.name === type);
                    return action && action.completed ? 1 : 0;
                }),
                backgroundColor: actionColors[type],
                borderColor: actionBorders[type],
                borderWidth: 1,
            };
        });

        setDailyStats({
            labels,
            datasets,
        });

        // Streak Calculation (kept same logic)
        let currentStreak = 0;
        const sortedHistory = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));

        for (const day of sortedHistory) {
            const completedCount = day.actions.filter(a => a.completed).length;
            if (completedCount >= 3) { // Slightly relaxed rule for "good day"
                currentStreak++;
            } else {
                break;
            }
        }
        setStreak(currentStreak);
    };

    const processQuranStats = (data, range) => {
        setQuranSummary({
            totalAyahs: data.totalAyahsRead,
            completedSurahs: data.completedSurahsCount,
        });

        const readings = data.dailyReadings || [];
        const ayahsMap = {};
        const surahsMap = {};

        readings.forEach(r => {
            const dateStr = new Date(r.date).toISOString().split('T')[0];
            ayahsMap[dateStr] = r.ayahsCount;
            // Count number of surahs read on that day
            surahsMap[dateStr] = r.surahsRead || [];
        });

        const labels = [];
        const ayahsData = [];
        const surahsData = [];
        const surahsNamesData = []; // Store names for tooltip

        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-indexed

        const getSurahNames = (surahIds) => {
            if (!surahIds || surahIds.length === 0) return [];
            return surahIds.map(id => {
                const s = surahList.find(surah => surah.number === id);
                return s ? s.name : `Sourate ${id}`;
            });
        };

        if (range === 'month') {
            // Current Calendar Month (1st to End of Month)
            const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

            for (let i = 1; i <= daysInMonth; i++) {
                const d = new Date(currentYear, currentMonth, i);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const dateStr = `${year}-${month}-${day}`;

                // Only show day number
                labels.push(d.toLocaleDateString('fr-FR', { day: 'numeric' }));
                ayahsData.push(ayahsMap[dateStr] || 0);

                const dailySurahs = surahsMap[dateStr] || [];
                surahsData.push(dailySurahs.length);
                surahsNamesData.push(getSurahNames(dailySurahs));
            }
        } else {
            // Current Calendar Year (Jan to Dec)
            // Aggregate readings by month first
            const monthlyAyahs = new Array(12).fill(0);
            const monthlySurahs = new Array(12).fill(0);
            const monthlySurahNames = new Array(12).fill(null).map(() => []);

            readings.forEach(r => {
                const d = new Date(r.date);
                if (d.getFullYear() === currentYear) {
                    const mIndex = d.getMonth();
                    monthlyAyahs[mIndex] += r.ayahsCount;

                    const dailySurahs = r.surahsRead || [];
                    monthlySurahs[mIndex] += dailySurahs.length;

                    // Accumulate names for the month
                    if (dailySurahs.length > 0) {
                        monthlySurahNames[mIndex].push(...getSurahNames(dailySurahs));
                    }
                }
            });

            for (let i = 0; i < 12; i++) {
                const d = new Date(currentYear, i, 1);
                labels.push(d.toLocaleDateString('fr-FR', { month: 'short' }));
                ayahsData.push(monthlyAyahs[i]);
                surahsData.push(monthlySurahs[i]);
                surahsNamesData.push(monthlySurahNames[i]);
            }
        }

        // Filter datasets based on view type
        const allDatasets = [
            {
                label: 'Versets lus',
                data: ayahsData,
                borderColor: themeColors.chart.ayahs,
                backgroundColor: themeColors.chart.ayahsBg,
                tension: 0.4,
                fill: true,
                yAxisID: 'y',
                hidden: range !== 'month' && range !== 'year' // just legacy fallback
            },
            {
                label: 'Sourates terminées',
                data: surahsData,
                surahNames: surahsNamesData, // Custom property for tooltip
                borderColor: themeColors.chart.surahs,
                backgroundColor: themeColors.chart.surahsBg,
                tension: 0.4,
                fill: true,
                yAxisID: 'y1', // Keep this valid logic locally
            }
        ];

        // Only include the dataset relevant to the current view
        const filteredDatasets = allDatasets.filter(ds => {
            if (quranViewType === 'ayahs') return ds.label === 'Versets lus';
            if (quranViewType === 'surahs') return ds.label === 'Sourates terminées';
            return true;
        });

        setQuranStats({
            labels,
            datasets: filteredDatasets,
        });
    };

    if (loading) return <div>Chargement des statistiques...</div>;

    const currentMonthName = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    const currentYearNum = new Date().getFullYear();

    return (
        <div className="stats-container page-transition">
            <h1>Tableau de Bord des Statistiques</h1>

            <div className="stats-grid">
                {/* Task Stats */}
                <div className="stat-card">
                    <h3>Progression des Tâches</h3>
                    <div className="chart-container">
                        {taskStats && <Doughnut data={taskStats} />}
                    </div>
                </div>

                {/* Daily Actions Chart (Stacked) */}
                <div className="stat-card wide-card">
                    <div className="stats-header-actions">
                        <h3>Détail des Actions Quotidiennes</h3>
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${timeRange === 'week' ? 'active' : ''}`}
                                onClick={() => setTimeRange('week')}
                            >
                                7 Jours
                            </button>
                            <button
                                className={`toggle-btn ${timeRange === 'month' ? 'active' : ''}`}
                                onClick={() => setTimeRange('month')}
                            >
                                30 Jours
                            </button>
                        </div>
                    </div>
                    <div className="chart-container wide">
                        {dailyStats && (
                            <Bar
                                data={dailyStats}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        x: { stacked: true },
                                        y: { stacked: true, beginAtZero: true, max: 5, ticks: { stepSize: 1 } }
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                label: function (context) {
                                                    return context.raw === 1 ? context.dataset.label : '';
                                                }
                                            }
                                        }
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Quran Stats */}
                <div className="stat-card full-width">
                    <div className="stats-header-actions">
                        <h3>Lecture du Coran ({quranTimeRange === 'month' ? currentMonthName : `Année ${currentYearNum}`})</h3>
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${quranTimeRange === 'month' ? 'active' : ''}`}
                                onClick={() => setQuranTimeRange('month')}
                            >
                                Mois
                            </button>
                            <button
                                className={`toggle-btn ${quranTimeRange === 'year' ? 'active' : ''}`}
                                onClick={() => setQuranTimeRange('year')}
                            >
                                Année
                            </button>
                        </div>
                    </div>

                    {/* View Toggle as Tabs */}
                    <div className="chart-type-tabs">
                        <button
                            onClick={() => setQuranViewType('ayahs')}
                            className="chart-tab-btn"
                            style={{
                                color: quranViewType === 'ayahs' ? themeColors.chart.ayahs : 'var(--text-secondary)',
                                borderBottomColor: quranViewType === 'ayahs' ? themeColors.chart.ayahs : 'transparent',
                                fontWeight: quranViewType === 'ayahs' ? '600' : '400',
                            }}
                        >
                            Versets
                        </button>
                        <button
                            onClick={() => setQuranViewType('surahs')}
                            className="chart-tab-btn"
                            style={{
                                color: quranViewType === 'surahs' ? themeColors.chart.surahs : 'var(--text-secondary)',
                                borderBottomColor: quranViewType === 'surahs' ? themeColors.chart.surahs : 'transparent',
                                fontWeight: quranViewType === 'surahs' ? '600' : '400',
                            }}
                        >
                            Sourates
                        </button>
                    </div>
                    <div className="stats-summary">
                        <div className="summary-item">
                            <span className="value" style={{ color: themeColors.primary }}>{quranSummary.totalAyahs}</span>
                            <span className="label">Versets lus</span>
                        </div>
                        <div className="summary-item">
                            <span className="value" style={{ color: themeColors.secondary }}>{quranSummary.completedSurahs}</span>
                            <span className="label">Sourates terminées</span>
                        </div>
                    </div>
                    <div className="chart-container wide">
                        {quranStats && <Line
                            data={quranStats}
                            options={{
                                responsive: true,
                                scales: {
                                    y: {
                                        type: 'linear',
                                        display: quranViewType === 'ayahs',
                                        position: 'left',
                                        beginAtZero: true,
                                        title: { display: true, text: 'Versets' }
                                    },
                                    y1: {
                                        type: 'linear',
                                        display: quranViewType === 'surahs',
                                        position: quranViewType === 'surahs' ? 'left' : 'right', // Move to left if it's the only one
                                        beginAtZero: true,
                                        grid: {
                                            drawOnChartArea: true,
                                        },
                                        title: { display: true, text: 'Sourates' },
                                        ticks: { stepSize: 1 }
                                    },
                                },
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            afterLabel: function (context) {
                                                if (context.dataset.label === 'Sourates terminées' && context.raw > 0) {
                                                    const names = context.dataset.surahNames && context.dataset.surahNames[context.dataIndex];
                                                    if (names && names.length > 0) {
                                                        return names.join(', ');
                                                    }
                                                }
                                                return null;
                                            }
                                        }
                                    }
                                }
                            }}
                        />}
                    </div>
                </div>


            </div>
        </div>
    );
};

export default StatsPage;
